// Sugalarm — full-screen photo meal flow (cycle 32).
//
// Replaces cycle 27's inline `PhotoCarbEstimator` card with a
// dedicated `/log/photo` route → preview → analyze → editable item list
// (tap-to-toggle inclusion, live total) → one-step
// `Log Xg of Carbs` save. On save we POST /api/treatments with the
// structured `provenance` column populated and bounce back to /log
// with a confirm pill threaded via `location.state.confirm`.
//
// Cycle 340: every real entry point (LogScreen, InsulinFlow) now hands a
// File straight to this screen via lib/photoHandoff and lands directly at
// `preview` — the `pick` phase below is a rarely-reached fallback (a
// direct deep-link to /log/photo with no handoff), kept for completeness
// with its own Take Photo / Choose from Library buttons.

import { prepareMealPhoto } from "../../lib/mealPhotoDownscale";
import { isHeicFile } from "../../lib/croppableImage";
import { mealPhotoErrorMessage } from "./mealPhotoMessage";
import { takeBolusUnits } from "./bolusPhotoHandoff";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import * as photoApi from "../../api/photo";
import * as treatmentsApi from "../../api/treatments";
import { CameraIcon } from "../../components/ui/icons";
import { Pill } from "../../components/ui/Pill";
import type { FrameVariant } from "../../hooks/useFrameVariant";
import { isKiosk } from "../../lib/auth";
import { isAndroidTabletClassRuntime } from "../../lib/deviceClass";
import { ACCEPT_IMAGE_CAMERA } from "../../lib/imageAccept";
import {
  clearPendingPhotoFile,
  peekPendingPhotoFile,
} from "../../lib/photoHandoff";
import {
  openPhotoInput,
  photoBlockedByAlarm,
  PHOTO_BLOCKED_LINE,
  usePhotoBlockedByAlarm,
} from "../../lib/nativeCamera";
import { cameraOnlyDevice } from "../../lib/photoSource";
import type { PhotoEstimate } from "../../api/photo";
import { BackButton } from "../../components/ui/BackButton";
import recentStyles from "./RecentEntriesScreen.module.css";
import styles from "./PhotoMealScreen.module.css";

const ACCEPT_MIME = "image/jpeg,image/png,image/webp,image/heic";
const REQUEST_TIMEOUT_MS = 30_000;

interface PhotoMealScreenProps {
  variant: FrameVariant;
}

type Phase =
  | { kind: "pick" }
  | { kind: "preview"; file: File; previewUrl: string }
  | { kind: "in_flight"; previewUrl: string }
  | {
      kind: "result";
      previewUrl: string;
      estimate: PhotoEstimate;
      includedItemIds: Set<number>;
      adjustedTotal: number;
    }
  | { kind: "saving"; previewUrl: string }
  | { kind: "error"; previewUrl: string | null; message: string };

function confidenceTone(c: PhotoEstimate["confidence"]) {
  if (c === "high") return "ok" as const;
  if (c === "medium") return "warn" as const;
  return "urgent" as const;
}



function sumIncluded(items: PhotoEstimate["items"], ids: Set<number>): number {
  let total = 0;
  for (let i = 0; i < items.length; i += 1) {
    if (ids.has(i)) total += items[i]?.carbs_g ?? 0;
  }
  return Math.max(0, Math.round(total));
}

function roundUpTo5(n: number): number {
  if (n <= 0) return 0;
  return Math.ceil(n / 5) * 5;
}

export function PhotoMealScreen({ variant }: PhotoMealScreenProps) {
  const nav = useNavigate();
  const location = useLocation();
  // Cycle 126: when launched from inside the guided Bolus food step the
  // estimator RETURNS the chosen carbs + item names into the in-progress
  // combined insulin row instead of saving a standalone carbs row. The
  // in-progress dose (units + product) round-trips through this state so
  // the user doesn't lose it.
  // Cycle 451.5: the entry carries only a token; the units themselves are
  // taken from a one-shot memory slot at hand-back (bolusPhotoHandoff.ts).
  const bolusReturn =
    (location.state as { bolusReturn?: { token?: string } } | null)
      ?.bolusReturn ?? null;
  const fileRef = useRef<HTMLInputElement>(null);
  const timeoutRef = useRef<number | null>(null);
  const lastBlobUrlRef = useRef<string | null>(null);
  // Cycle 202: the Android-tablet kiosk gets the streamlined flow — no dead
  // "pick" landing (the Carbs CTA already opened the chooser and handed a
  // File here via the module holder), 16:9 framing, and a two-column
  // no-scroll result. Same gate the LogScreen CTA / native half use; the
  // Pi/iPad kiosk, web, and mobile keep the single-column flow + landing.
  const androidTablet = variant === "kiosk" && isAndroidTabletClassRuntime();
  // Enter directly at `preview` when the CTA handed us a File. The state
  // initializer only PEEKS the holder (pure — safe under StrictMode's
  // double-invoke); the mount effect below clears it once and creates the
  // blob URL. We start in a transient preview with an empty previewUrl so
  // there is no flash of the landing page.
  const [phase, setPhase] = useState<Phase>(() => {
    const handoff = peekPendingPhotoFile();
    return handoff
      ? { kind: "preview", file: handoff, previewUrl: "" }
      : { kind: "pick" };
  });
  const [roundTo5, setRoundTo5] = useState<boolean>(false);

  // Kiosk session lockout — backend would 403 anyway, but the trigger
  // on LogScreen is already kiosk-gated; this is the deep-link guard.
  // Cycle 200: the Android tablet kiosk is allowed (rear camera + native
  // in-app capture); the Pi kiosk and the iPad kiosk stay blocked.
  const kioskBlocked =
    (variant === "kiosk" || isKiosk()) && !isAndroidTabletClassRuntime();

  const revokeLastBlob = useCallback(() => {
    if (lastBlobUrlRef.current) {
      URL.revokeObjectURL(lastBlobUrlRef.current);
      lastBlobUrlRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      revokeLastBlob();
    };
  }, [revokeLastBlob]);

  // Cycle 202: adopt the handed-off File exactly once — clear the holder
  // and create the blob URL here (a side effect, kept out of the render
  // initializer so StrictMode's double-invoke can't double-create it).
  // Mounting with the empty-previewUrl `preview` phase means no flash of
  // the landing page; this fills in the image src on the first effect tick.
  //
  // Cycle 455 (the cycle-202 dev-only defect): the File is held in a ref taken
  // at the FIRST render, not re-read from the holder here. Under dev
  // StrictMode the mount/unmount/mount replay runs the unmount cleanup above
  // (revoking this effect's URL) and then this effect again — which used to
  // find the holder already cleared and leave the preview on a REVOKED blob
  // URL. Now the replay makes a fresh URL from the same File. A URL is still
  // revoked only when it is replaced or on unmount (it rides on into the
  // in_flight / result / saving / error phases). Production runs this once,
  // exactly as before.
  const handedFileRef = useRef<File | null>(peekPendingPhotoFile());
  useEffect(() => {
    const handoff = handedFileRef.current;
    clearPendingPhotoFile();
    if (!handoff) return;
    const url = URL.createObjectURL(handoff);
    lastBlobUrlRef.current = url;
    setPhase({ kind: "preview", file: handoff, previewUrl: url });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goBack = () => {
    if (window.history.length > 1) nav(-1);
    else nav("/log", { replace: true });
  };

  // Cycle 455: on the kiosk, every photo button here is disabled while any
  // alarm sounds (lib/nativeCamera). The library/retake input keeps today's
  // native route (no purpose → the "Add a meal photo" dialog); the Take
  // Photo input sends meal_photo, so the APK opens the REAR camera directly
  // instead of routing its capture="environment" to the front camera.
  const photoBlocked = usePhotoBlockedByAlarm();
  // Cycle 458: the kiosk APK is camera-only (lib/photoSource). There the
  // library input is not mounted, and every "pick again" button opens the
  // camera (with its meal_photo purpose) — the library input carries no
  // `capture`, so on the kiosk it reached the native "Choose from gallery"
  // dialog.
  const cameraOnly = cameraOnlyDevice();
  const openPicker = () => {
    if (photoBlockedByAlarm()) return;
    if (cameraOnly) {
      openPhotoInput(cameraFileRef.current, "meal_photo");
      return;
    }
    fileRef.current?.click();
  };

  // Cycle 340: a direct "Take Photo" target alongside the existing
  // gallery/OS-chooser input, so the (now rarely-reached — see cycle-202+
  // handoff comment above) landing screen still opens the camera in one
  // tap rather than routing through an OS action-sheet by default.
  const cameraFileRef = useRef<HTMLInputElement>(null);
  const openCamera = () => {
    if (photoBlockedByAlarm()) return;
    openPhotoInput(cameraFileRef.current, "meal_photo");
  };

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Clear the input so picking the same file again still fires change.
    e.target.value = "";
    if (!file) return;
    revokeLastBlob();
    const url = URL.createObjectURL(file);
    lastBlobUrlRef.current = url;
    setPhase({ kind: "preview", file, previewUrl: url });
  };

  const analyze = async () => {
    if (phase.kind !== "preview") return;
    const file = phase.file;
    const previewUrl = phase.previewUrl;
    setPhase({ kind: "in_flight", previewUrl });
    // Cycle 457: made small on the device first (at most 1024 px, a fresh
    // JPEG; HEIC converted) — tens of kilobytes instead of megabytes. Never
    // throws: on any failure the original goes and the server cleans it.
    const prepared = await prepareMealPhoto(file);
    let timedOut = false;
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutRef.current = window.setTimeout(() => {
        timedOut = true;
        reject(new Error("timeout"));
      }, REQUEST_TIMEOUT_MS);
    });
    try {
      const result = await Promise.race([
        photoApi.estimateCarbs(prepared),
        timeoutPromise,
      ]);
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      if (!result.ok) {
        setPhase({
          kind: "error",
          previewUrl,
          message: mealPhotoErrorMessage(
            result.error.status,
            false,
            result.error.message,
            isHeicFile(file),
          ),
        });
        return;
      }
      const estimate = result.data;
      const allIds = new Set<number>(estimate.items.map((_, i) => i));
      setPhase({
        kind: "result",
        previewUrl,
        estimate,
        includedItemIds: allIds,
        adjustedTotal: sumIncluded(estimate.items, allIds),
      });
    } catch (e) {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      setPhase({
        kind: "error",
        previewUrl,
        message: timedOut
          ? mealPhotoErrorMessage(0, true)
          : e instanceof Error && e.message !== "timeout"
            ? "Estimate failed — try again or count manually."
            : mealPhotoErrorMessage(0, true),
      });
    }
  };

  const toggleItem = (idx: number) => {
    setPhase((p) => {
      if (p.kind !== "result") return p;
      const next = new Set(p.includedItemIds);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return {
        ...p,
        includedItemIds: next,
        adjustedTotal: sumIncluded(p.estimate.items, next),
      };
    });
  };

  const submit = async () => {
    if (phase.kind !== "result") return;
    if (phase.estimate.confidence === "low") return;
    const finalTotal = roundTo5 ? roundUpTo5(phase.adjustedTotal) : phase.adjustedTotal;
    // Cycle 202.1: a 0 g total is a VALID log — water, a bubbler, diet soda,
    // black coffee, plain meat are real zero-carb items worth recording (the
    // manual Carbs entry already allows 0). sumIncluded() floors at 0, so this
    // only rejects an impossible negative.
    if (finalTotal < 0) return;
    const itemNames = phase.estimate.items
      .filter((_, i) => phase.includedItemIds.has(i))
      .map((it) => it.name)
      .filter((s) => s && s.trim().length > 0);
    const previewUrl = phase.previewUrl;

    // Cycle 126: Bolus food-step return — hand the carbs + names + the
    // photo provenance back to the in-progress combined insulin row
    // rather than saving a standalone carbs treatment.
    if (bolusReturn) {
      nav("/log?tab=insulin", {
        replace: true,
        state: {
          bolusPhoto: {
            carbs_g: finalTotal,
            description: itemNames.join(", "),
            provenance: {
              source: "photo",
              model: phase.estimate.model,
              request_id: phase.estimate.request_id,
              confidence: phase.estimate.confidence,
            },
            ...(() => {
              const carried = takeBolusUnits(bolusReturn.token);
              return { units: carried?.units ?? "", label: carried?.label ?? "" };
            })(),
          },
        },
      });
      return;
    }

    setPhase({ kind: "saving", previewUrl });
    const r = await treatmentsApi.create({
      event_type: "carbs",
      timestamp: new Date().toISOString(),
      carbs_g: finalTotal,
      notes: itemNames.length > 0 ? itemNames.join(", ") : null,
      provenance: {
        source: "photo",
        model: phase.estimate.model,
        request_id: phase.estimate.request_id,
        confidence: phase.estimate.confidence,
      },
    });
    if (!r.ok) {
      setPhase({
        kind: "error",
        previewUrl,
        message:
          r.error.status === 0
            ? "Network hiccup — try again."
            : "Couldn't save — try again or count manually.",
      });
      return;
    }
    // Cycle 34.19: pass a richer `congrats` body alongside the
    // existing `confirm` pill text so LogScreen's mobile path can
    // render a personalized "You logged Cheeseburger with 60g of
    // carbs." message inside the celebratory modal.
    const congrats =
      itemNames.length > 0
        ? `You logged ${itemNames.join(", ")} with ${finalTotal}g of carbs.`
        : `You logged ${finalTotal}g of carbs from a photo.`;
    // Cycle 451.3: return to the Carbs tab, where the entry just saved is.
    // `kind=carbs` is an intent Log's allowlist already accepts (the same
    // one the "count manually" links below use). A bare /log would open on
    // Insulin (the cycle-451.2 default).
    nav("/log?kind=carbs", {
      replace: true,
      state: { confirm: "Logged carbs from photo.", congrats },
    });
  };

  const displayTotal = useMemo(() => {
    if (phase.kind !== "result") return 0;
    return roundTo5 ? roundUpTo5(phase.adjustedTotal) : phase.adjustedTotal;
  }, [phase, roundTo5]);

  // Cycle 202: the result-phase pieces are built once as shared nodes so
  // the single-column (web / mobile / Pi-iPad kiosk) layout and the
  // Android-tablet two-column layout render the IDENTICAL controls — every
  // feature (tap-to-toggle items, Total, Round-to-5, low-confidence
  // Log-disable + Count manually, the one-step save) lives in one place
  // and can't drift between the two layouts.
  const resultPhase = phase.kind === "result" ? phase : null;
  const confidenceMetaNode = resultPhase ? (
    <div className={styles.resultHeader}>
      <Pill tone={confidenceTone(resultPhase.estimate.confidence)}>
        {resultPhase.estimate.confidence} confidence
      </Pill>
      <span className={styles.modelHint}>via {resultPhase.estimate.model}</span>
    </div>
  ) : null;
  const caveatNode = (
    <div className={styles.caveat}>
      Estimates aren't precise enough for tight-control dosing — review before
      saving.
    </div>
  );
  const notesNode =
    resultPhase && resultPhase.estimate.notes ? (
      <div className={styles.modelNotes}>{resultPhase.estimate.notes}</div>
    ) : null;
  const itemsNode = resultPhase ? (
    resultPhase.estimate.items.length === 0 ? (
      <div className={styles.emptyItems}>No clearly identified items.</div>
    ) : (
      <div className={styles.items}>
        {resultPhase.estimate.items.map((it, idx) => {
          const included = resultPhase.includedItemIds.has(idx);
          return (
            <button
              key={`${it.name}-${idx}`}
              type="button"
              role="checkbox"
              aria-checked={included}
              onClick={() => toggleItem(idx)}
              className={`${styles.itemRow} ${included ? "" : styles.itemRowOff}`}
            >
              <span
                className={`${styles.checkbox} ${included ? styles.checkboxOn : ""}`}
                aria-hidden
              >
                {included ? "✓" : ""}
              </span>
              <span className={styles.itemBody}>
                <span className={styles.itemName}>{it.name}</span>
                {it.portion && (
                  <span className={styles.itemPortion}>{it.portion}</span>
                )}
              </span>
              <span
                className={`${styles.itemCarbs} ${included ? "" : styles.itemCarbsOff}`}
              >
                {Math.round(it.carbs_g)}g
              </span>
            </button>
          );
        })}
      </div>
    )
  ) : null;
  const totalRoundNode = resultPhase ? (
    <>
      <div className={styles.totalRow}>
        <span className={styles.totalLabel}>Total</span>
        <span className={styles.totalValue}>{displayTotal}g</span>
      </div>
      <label className={styles.roundRow}>
        <input
          type="checkbox"
          checked={roundTo5}
          onChange={(e) => setRoundTo5(e.target.checked)}
        />
        <span>Round to nearest 5g</span>
      </label>
    </>
  ) : null;
  const logActionNode = resultPhase ? (
    resultPhase.estimate.confidence === "low" ? (
      <>
        <button type="button" className={styles.primaryBtnDisabled} disabled>
          Log {displayTotal}g of Carbs
        </button>
        <div className={styles.lowHint}>
          Photo wasn't clear enough — please count manually.
        </div>
        <button
          type="button"
          className={styles.tertiaryBtn}
          onClick={() => nav("/log?kind=carbs", { replace: true })}
        >
          Count manually ›
        </button>
      </>
    ) : (
      <button
        type="button"
        className={styles.primaryBtn}
        onClick={submit}
      >
        {bolusReturn
          ? `Use ${displayTotal}g for this bolus`
          : `Log ${displayTotal}g of Carbs`}
      </button>
    )
  ) : null;

  // ─── Kiosk lockout ────────────────────────────────────────────────
  if (kioskBlocked) {
    return (
      <div className={recentStyles.root}>
        <div className={recentStyles.header}>
          <BackButton label="Log" onBack={goBack} solid />
          <span className={recentStyles.title}>Photo meal</span>
        </div>
        <div className={recentStyles.body}>
          <div className={styles.kioskMsg}>
            Photo meal isn't available on this device — count manually.
          </div>
        </div>
      </div>
    );
  }

  // Cycle 202: on the Android tablet the result phase moves its photo into
  // the left column of the two-column layout, so the shared top photo
  // surface is suppressed there (every other phase + every other variant
  // keeps it). Empty previewUrl (the one-tick handoff transient) renders no
  // src so there's no broken-image flash — only the bg-sunk frame shows.
  const showTopPhotoSurface = !(androidTablet && phase.kind === "result");

  return (
    <div
      className={`${recentStyles.root} ${
        androidTablet ? styles.androidTablet : ""
      }`}
    >
      {cameraOnly ? null : (
        <input
          ref={fileRef}
          type="file"
          accept={ACCEPT_MIME}
          onChange={onFileChange}
          className={styles.hiddenInput}
        />
      )}
      <input
        ref={cameraFileRef}
        type="file"
        accept={ACCEPT_IMAGE_CAMERA}
        capture="environment"
        onChange={onFileChange}
        className={styles.hiddenInput}
      />

      <div className={recentStyles.header}>
        {/* Cycle 162: shared BackButton, same goBack handler. Cycle 202.2:
            `solid` → the purple CTA pill (matching the View Log pill + the
            sibling /log/recent back button; recentStyles.root already scopes
            the --cta-purple-* tokens). */}
        <BackButton label="Log" onBack={goBack} solid />
        <span className={recentStyles.title}>Photo meal</span>
      </div>

      <div className={`${recentStyles.body} ${styles.body}`}>
        {/* Cycle 455: while an alarm sounds on the kiosk every photo button
            here is disabled, and this line says why (a disabled control is
            never silent). Only ever rendered on the kiosk, during an alarm, and
            only in the phases that show a photo button — never on the
            no-scroll result layout. */}
        {photoBlocked &&
          (phase.kind === "pick" || phase.kind === "preview" || phase.kind === "error") && (
          <div className={styles.photoBlockedNote} role="status">
            {PHOTO_BLOCKED_LINE}
          </div>
        )}
        {/* ─── Photo / trigger surface ─────────────────────────────── */}
        {showTopPhotoSurface &&
          (phase.kind === "pick" ? (
            <div className={styles.pickWrap}>
              <button
                type="button"
                className={styles.triggerBtn}
                onClick={openCamera}
                disabled={photoBlocked}
              >
                <CameraIcon className={styles.triggerIcon} />
                <span className={styles.triggerLabel}>Take Photo</span>
              </button>
              {cameraOnly ? null : (
                <button
                  type="button"
                  className={styles.triggerBtnSecondary}
                  onClick={openPicker}
                  disabled={photoBlocked}
                >
                  <span className={styles.triggerLabel}>
                    🖼️ Choose from Library
                  </span>
                </button>
              )}
            </div>
          ) : (
            <div className={styles.previewWrap}>
              <img
                src={phase.previewUrl || undefined}
                alt="Meal preview"
                className={styles.previewImg}
              />
            </div>
          ))}

        {/* ─── Preview phase actions ───────────────────────────────── */}
        {phase.kind === "preview" && (
          <div className={styles.actionRow}>
            <button
              type="button"
              className={styles.secondaryBtn}
              onClick={openPicker}
                disabled={photoBlocked}
            >
              {cameraOnly ? "Retake" : "Retake or upload"}
            </button>
            <button
              type="button"
              className={styles.primaryBtn}
              onClick={analyze}
            >
              Analyze
            </button>
          </div>
        )}

        {/* ─── In-flight phase ─────────────────────────────────────── */}
        {phase.kind === "in_flight" && (
          <div className={styles.inFlightRow}>
            <span className={styles.spinner} aria-hidden="true" />
            <span>Estimating carbs…</span>
          </div>
        )}

        {/* ─── Result phase ────────────────────────────────────────── */}
        {/* Cycle 202: Android tablet → a landscape TWO-COLUMN, no-scroll
            layout (left = 16:9 photo + confidence/meta + caveat + notes;
            right glass card = item rows [the only thing that may scroll] +
            Total + Round + Log, all pinned visible). Every other surface
            keeps the single-column stack. Both render the SAME shared
            nodes, so no feature can drift between them. */}
        {phase.kind === "result" && androidTablet ? (
          <div className={styles.resultTwoCol}>
            <div className={`${styles.resultLeft} ${styles.glass}`}>
              <div className={styles.previewWrap}>
                <img
                  src={phase.previewUrl || undefined}
                  alt="Meal preview"
                  className={styles.previewImg}
                />
              </div>
              {confidenceMetaNode}
              {caveatNode}
              {notesNode}
            </div>
            <div className={`${styles.resultRight} ${styles.glass}`}>
              <div className={styles.itemsScroll}>{itemsNode}</div>
              {totalRoundNode}
              {logActionNode}
            </div>
          </div>
        ) : phase.kind === "result" ? (
          <>
            {caveatNode}
            {confidenceMetaNode}
            {itemsNode}
            {totalRoundNode}
            {notesNode}
            {logActionNode}
          </>
        ) : null}

        {/* ─── Saving phase ────────────────────────────────────────── */}
        {phase.kind === "saving" && (
          <button type="button" className={styles.primaryBtn} disabled>
            <span className={styles.spinner} aria-hidden="true" /> Logging…
          </button>
        )}

        {/* ─── Error phase ─────────────────────────────────────────── */}
        {phase.kind === "error" && (
          <>
            <div className={styles.errorMsg}>{phase.message}</div>
            <div className={styles.actionRow}>
              <button
                type="button"
                className={styles.secondaryBtn}
                onClick={openPicker}
                disabled={photoBlocked}
              >
                Try a different photo
              </button>
              <button
                type="button"
                className={styles.tertiaryBtn}
                onClick={() => nav("/log?kind=carbs", { replace: true })}
              >
                Count manually
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
